import { useState, useEffect, Component } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { FaWhatsapp, FaDownload, FaCircle, FaSignOutAlt } from 'react-icons/fa';

class ErrorBoundary extends Component<{children: React.ReactNode}, {error: string | null}> {
  constructor(props: any) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e: any) { return { error: e?.message || String(e) }; }
  render() {
    if (this.state.error) return (
      <div className="max-w-2xl mx-auto mt-10 p-6 bg-red-50 border border-red-200 rounded-xl">
        <h2 className="font-bold text-red-800 mb-2">Render Error</h2>
        <pre className="text-xs text-red-700 whitespace-pre-wrap">{this.state.error}</pre>
        <button onClick={() => this.setState({error: null})} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg text-sm">Retry</button>
      </div>
    );
    return this.props.children;
  }
}

interface Lead {
  id: number;
  name: string;
  phone: string;
  cutoff_mark: number | null;
  tnea_rank: number | null;
  community: string;
  college_code: string | null;
  college_name: string | null;
  status: 'new' | 'contacted' | 'converted' | 'not_interested';
  notes: string | null;
  created_at: string;
}

const STATUS_STYLES = {
  new:            { dot: 'text-blue-500',   bg: 'bg-blue-50 text-blue-700 border-blue-200',   label: 'New' },
  contacted:      { dot: 'text-yellow-500', bg: 'bg-yellow-50 text-yellow-700 border-yellow-200', label: 'Contacted' },
  converted:      { dot: 'text-green-500',  bg: 'bg-green-50 text-green-700 border-green-200',  label: 'Converted' },
  not_interested: { dot: 'text-gray-400',   bg: 'bg-gray-50 text-gray-500 border-gray-200',   label: 'Not Interested' },
};

const WHATSAPP_NUMBER = '918122552867';

const shortCollege = (name: string) =>
  (name || '')
    .replace(/\(Autonomous\)/gi, '')
    .replace(/\([^)]*\)/g, '')
    .replace(/,\s*[A-Z][^,]*District[^,]*/gi, '')
    .replace(/,\s*\d{6}.*$/g, '')
    .trim();

const AdminLeads = () => {
  const { logout, username } = useAuth();
  const [leads, setLeads]       = useState<Lead[]>([]);
  const [loading, setLoading]   = useState(true);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(1);

  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0, not_interested: 0 });

  const [filterStatus, setFilterStatus]   = useState('');
  const [filterFrom, setFilterFrom]       = useState('');
  const [filterTo, setFilterTo]           = useState('');

  const [editingId, setEditingId]   = useState<number | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes]   = useState('');

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchLeads(); }, [page, filterStatus, filterFrom, filterTo]);

  const fetchStats = async () => {
    try {
      const res: any = await axios.get('/leads/stats');
      setStats({ total: res.total || 0, new: res.new || 0, contacted: res.contacted || 0, converted: res.converted || 0, not_interested: res.not_interested || 0 });
    } catch {}
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20 };
      if (filterStatus) params.status = filterStatus;
      if (filterFrom)   params.from   = filterFrom;
      if (filterTo)     params.to     = filterTo;
      const res: any = await axios.get('/leads', { params });
      setLeads(res.leads || []);
      setTotal(res.total || 0);
      setPages(res.pages || 1);
    } catch { setLeads([]); }
    finally { setLoading(false); }
  };

  const saveStatus = async (id: number) => {
    await axios.patch(`/leads/${id}/status`, { status: editStatus, notes: editNotes }).catch(() => {});
    setEditingId(null);
    fetchLeads();
    fetchStats();
  };

  const startEdit = (lead: Lead) => {
    setEditingId(lead.id);
    setEditStatus(lead.status);
    setEditNotes(lead.notes || '');
  };

  const exportCSV = () => {
    const params = new URLSearchParams();
    if (filterStatus) params.set('status', filterStatus);
    if (filterFrom)   params.set('from', filterFrom);
    if (filterTo)     params.set('to', filterTo);
    window.open(`/api/v1/leads/export?${params.toString()}`, '_blank');
  };

  const whatsappLink = (lead: Lead) => {
    const score = [
      lead.cutoff_mark ? `Cutoff: ${lead.cutoff_mark}/200` : '',
      lead.tnea_rank   ? `Rank: ${lead.tnea_rank.toLocaleString()}` : '',
    ].filter(Boolean).join(', ');
    const college = lead.college_name ? shortCollege(lead.college_name) : '';
    const msg = `Hi ${lead.name}, this is from ChooseMyCollege. You enquired about ${college || 'TNEA admissions'} (${score}, ${lead.community}). I can help you find the best colleges for your score. Are you available for a quick call?`;
    return `https://wa.me/91${lead.phone}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <>
      <Helmet>
        <title>Leads — Admin | ChooseMyCollege</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 px-4 py-6">
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Student Leads</h1>
            <p className="text-sm text-gray-500 mt-0.5">{total} total enquiries · logged in as <strong>{username}</strong></p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportCSV}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-colors shadow"
            >
              <FaDownload className="text-xs" /> Export CSV
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold px-4 py-2.5 rounded-xl text-sm transition-colors"
            >
              <FaSignOutAlt className="text-xs" /> Logout
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Leads',    value: stats.total,         color: 'text-blue-600'  },
            { label: 'New',            value: stats.new,           color: 'text-blue-500'  },
            { label: 'Contacted',      value: stats.contacted,     color: 'text-yellow-600'},
            { label: 'Converted',      value: stats.converted,     color: 'text-green-600' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 text-center">
              <div className={`text-3xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg">
                <option value="">All</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="converted">Converted</option>
                <option value="not_interested">Not Interested</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">From</label>
              <input type="date" value={filterFrom} onChange={e => { setFilterFrom(e.target.value); setPage(1); }}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">To</label>
              <input type="date" value={filterTo} onChange={e => { setFilterTo(e.target.value); setPage(1); }}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            </div>
            <button onClick={() => { setFilterStatus(''); setFilterFrom(''); setFilterTo(''); setPage(1); }}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg">
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-gray-400">Loading...</div>
          ) : leads.length === 0 ? (
            <div className="py-16 text-center text-gray-400">No leads found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">#</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Student</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Score</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">College Viewed</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {leads.map(lead => {
                    const s = STATUS_STYLES[lead.status] ?? STATUS_STYLES['new'];
                    const isEditing = editingId === lead.id;
                    return (
                      <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 text-gray-400 text-xs">{lead.id}</td>
                        <td className="py-3 px-4">
                          <div className="font-semibold text-gray-900">{lead.name}</div>
                          <div className="text-gray-500 text-xs">{lead.phone}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-xs space-y-0.5">
                            {lead.cutoff_mark != null && <div><span className="text-gray-400">Cutoff:</span> <span className="font-medium">{lead.cutoff_mark}/200</span></div>}
                            {lead.tnea_rank    != null && <div><span className="text-gray-400">Rank:</span> <span className="font-medium">{lead.tnea_rank.toLocaleString()}</span></div>}
                            <div className="mt-0.5">
                              <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[10px] font-semibold">{lead.community}</span>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 max-w-[200px]">
                          {lead.college_name ? (
                            <div>
                              <div className="text-xs font-medium text-gray-800 leading-snug">{shortCollege(lead.college_name)}</div>
                              <div className="text-[10px] text-gray-400">{lead.college_code}</div>
                            </div>
                          ) : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                        <td className="py-3 px-4">
                          {isEditing ? (
                            <div className="space-y-2 min-w-[160px]">
                              <select value={editStatus} onChange={e => setEditStatus(e.target.value)}
                                className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg">
                                <option value="new">New</option>
                                <option value="contacted">Contacted</option>
                                <option value="converted">Converted</option>
                                <option value="not_interested">Not Interested</option>
                              </select>
                              <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)}
                                placeholder="Add notes..." rows={2}
                                className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg resize-none" />
                              <div className="flex gap-1">
                                <button onClick={() => saveStatus(lead.id)}
                                  className="flex-1 bg-blue-600 text-white text-xs py-1 rounded-lg hover:bg-blue-700">Save</button>
                                <button onClick={() => setEditingId(null)}
                                  className="flex-1 bg-gray-100 text-gray-600 text-xs py-1 rounded-lg hover:bg-gray-200">Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${s.bg}`}>
                                <FaCircle className={`text-[6px] ${s.dot}`} />{s.label}
                              </span>
                              {lead.notes && <p className="text-[10px] text-gray-400 mt-1 max-w-[140px] truncate">{lead.notes}</p>}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4 text-xs text-gray-400 whitespace-nowrap">
                          {new Date(lead.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                          <div>{new Date(lead.created_at).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col gap-1.5">
                            <a href={whatsappLink(lead)} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white text-[10px] font-semibold px-2.5 py-1.5 rounded-lg transition-colors whitespace-nowrap">
                              <FaWhatsapp /> Reply
                            </a>
                            {!isEditing && (
                              <button onClick={() => startEdit(lead)}
                                className="inline-flex items-center justify-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-600 text-[10px] font-semibold px-2.5 py-1.5 rounded-lg transition-colors">
                                Update
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
              ← Prev
            </button>
            <span className="text-sm text-gray-500">Page {page} of {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p+1))} disabled={page === pages}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
              Next →
            </button>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

const AdminLeadsPage = () => (
  <ErrorBoundary><AdminLeads /></ErrorBoundary>
);

export default AdminLeadsPage;
